import { AfterViewInit, Component, TemplateRef, ViewChild, input, output } from '@angular/core';

import { filter, map, of } from 'rxjs';

import { DropdownComponent, InputChoicesComponent } from '@camelot/form-input';
import { InputChoices, InputDropdown } from '@camelot/form-model';
import { FontIconComponent } from '@camelot/icons';
import { UserLogoComponent } from '@camelot/ui';
import { CamBaseComponent, TypedTemplateDirective, extractEnum, fullName, isNonNullable } from '@camelot/utils';

import { CommunicationParticipantExtended, CommunicationType } from '../../services/dto/communication';
import { ParticipantsListComponent } from '../participants-list/participants-list.component';

export type Filter = {
  participants?: string[];
  messageType?: CommunicationType[] | null;
};

@Component({
  selector: 'cam-conversation-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  imports: [
    FontIconComponent,
    InputChoicesComponent,
    DropdownComponent,
    UserLogoComponent,
    ParticipantsListComponent,
    TypedTemplateDirective,
  ],
})
export class FiltersComponent extends CamBaseComponent implements AfterViewInit {
  conversationParticipants = input.required<CommunicationParticipantExtended[]>();

  filtersUpdated = output<Filter>();

  @ViewChild('participantTemplate', { read: TemplateRef })
  participantTemplate!: TemplateRef<void>;
  readonly participantToken!: {
    item: {
      firstName: string;
      lastName: string;
      picture?: string;
      trigram: string;
    };
  };
  private _participantsValue: string[] = [];
  public participantsInput: InputChoices | null = null;

  @ViewChild('typeTemplate', { read: TemplateRef })
  typeTemplate!: TemplateRef<void>;
  readonly typeToken!: { item: CommunicationType };
  private _typeValue: CommunicationType[] | null = null;
  public typeInput: InputDropdown<string[]> | null = null;

  public open = false;

  ngAfterViewInit(): void {
    const form = new InputChoices({
      key: 'participants',
      label: 'communication.filters.participants',
      multiple: true,
      options: of(this.conversationParticipants()).pipe(
        filter(isNonNullable),
        map(list =>
          list.map(item => {
            if (item.user) {
              return {
                id: item.user.id,
                name: fullName({
                  firstName: item.user.firstName,
                  name: item.user.lastName,
                }),
                data: item.user,
              };
            }
            if (item.contact) {
              return {
                id: item.contact.id,
                name: fullName({
                  firstName: item.contact.firstName,
                  name: item.contact.lastName,
                }),
                data: item.contact,
              };
            }
            return {
              id: item.email,
              name: item.email,
              data: {
                firstName: item.email,
                lastName: '',
                trigram: item.email[0],
              },
            };
          })
        )
      ),
      choiceTemplate: { one: this.participantTemplate },
    });
    form.createFormControl();
    this.participantsInput = form;
    this._registerSubscription(
      this.participantsInput.changeValue$.subscribe({
        next: values => {
          this._participantsValue = values;
          this._dispatchFilters();
        },
      })
    );

    let options = extractEnum(CommunicationType, true).map(item => ({
      id: item.value.toString(),
      name: `${item.name.toLowerCase()}`,
    }));
    this.typeInput = new InputDropdown<string[]>({
      key: 'type',
      label: 'communication.filters.type',
      multiple: true,
      options: of(options),
      value: options.map(item => item.id.toString()),
    });
    this.typeInput.createFormControl();
    this._registerSubscription(
      this.typeInput.changeValue$.subscribe({
        next: values => {
          this._typeValue = values.map(value => Number(value));
          this._dispatchFilters();
        },
      })
    );
  }

  ngOnDestoy() {
    this.participantsInput?.destroy();
  }

  private _dispatchFilters() {
    this.filtersUpdated.emit({
      participants: this._participantsValue,
      messageType: this._typeValue,
    });
  }
}
